/**
 * useSubscription — React hook for LOGOS HUMANO premium subscription management
 * 
 * Bridges to native StoreKit 2 via LOGOSSubscriptionPlugin on iOS.
 * On web/non-native platforms, defaults to non-premium (free) mode.
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 */

import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import LOGOSSubscription, {
  PREMIUM_PRODUCT_ID,
  type SubscriptionProduct,
  type SubscriptionStatus,
} from '../plugins/logos-subscription';

export interface SubscriptionState {
  /** Whether the user has an active premium subscription */
  isPremium: boolean;
  /** Whether we're still loading the subscription status */
  loading: boolean;
  /** Available products from App Store */
  products: SubscriptionProduct[];
  /** Active product ID (if subscribed) */
  productId: string;
  /** Subscription expiration date (ISO string) */
  expirationDate: string;
  /** Whether the subscription will auto-renew */
  willAutoRenew: boolean;
  /** Error message if any operation failed */
  error: string | null;
  /** Whether a purchase is in progress */
  purchasing: boolean;
}

const CACHE_KEY = 'logos-subscription-status';

const ADMIN_EMAILS = new Set(['jose@manuelcadena.com']);
const TRIAL_DAYS = 15;

export function useSubscription(userEmail?: string, userCreatedAt?: string | null) {
  const isNative = Capacitor.isNativePlatform();

  const [state, setState] = useState<SubscriptionState>(() => {
    // Try to load cached status for instant UI
    if (typeof window !== 'undefined') {
      try {
        const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
        if (cached.isPremium !== undefined) {
          return {
            isPremium: cached.isPremium,
            loading: true, // still verify with StoreKit
            products: [],
            productId: cached.productId || '',
            expirationDate: cached.expirationDate || '',
            willAutoRenew: cached.willAutoRenew || false,
            error: null,
            purchasing: false,
          };
        }
      } catch { /* ignore */ }
    }
    return {
      isPremium: false,
      loading: true,
      products: [],
      productId: '',
      expirationDate: '',
      willAutoRenew: false,
      error: null,
      purchasing: false,
    };
  });

  // Check subscription status on mount
  useEffect(() => {
    if (!isNative) {
      // Web: admin bypass
      const isAdmin = !!userEmail && ADMIN_EMAILS.has(userEmail.toLowerCase());
      // 15-day free trial for registered users
      let isTrial = false;
      let trialDaysLeft = 0;
      if (!isAdmin && userCreatedAt) {
        const created = new Date(userCreatedAt).getTime();
        const now = Date.now();
        const elapsed = (now - created) / (1000 * 60 * 60 * 24);
        trialDaysLeft = Math.max(0, Math.ceil(TRIAL_DAYS - elapsed));
        isTrial = trialDaysLeft > 0;
      }
      setState(s => ({ ...s, isPremium: isAdmin || isTrial, loading: false }));
      return;
    }

    let cancelled = false;

    async function checkStatus() {
      try {
        const [status, productsResult] = await Promise.all([
          LOGOSSubscription.getSubscriptionStatus(),
          LOGOSSubscription.getProducts().catch(() => ({ products: [] })),
        ]);

        if (cancelled) return;

        const newState = {
          isPremium: status.isPremium,
          loading: false,
          products: productsResult.products,
          productId: status.productId,
          expirationDate: status.expirationDate,
          willAutoRenew: status.willAutoRenew,
          error: null,
          purchasing: false,
        };

        setState(newState);

        // Cache for instant UI on next launch
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          isPremium: status.isPremium,
          productId: status.productId,
          expirationDate: status.expirationDate,
          willAutoRenew: status.willAutoRenew,
        }));
      } catch (err: any) {
        if (cancelled) return;
        setState(s => ({ ...s, loading: false, error: err.message }));
      }
    }

    checkStatus();

    return () => { cancelled = true; };
  }, [isNative, userEmail, userCreatedAt]);

  // Purchase the premium subscription
  const purchase = useCallback(async () => {
    if (!isNative) return false;

    setState(s => ({ ...s, purchasing: true, error: null }));

    try {
      const result = await LOGOSSubscription.purchase({ productId: PREMIUM_PRODUCT_ID });

      if (result.success) {
        setState(s => ({
          ...s,
          isPremium: true,
          purchasing: false,
          productId: result.productId || PREMIUM_PRODUCT_ID,
        }));
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          isPremium: true,
          productId: result.productId || PREMIUM_PRODUCT_ID,
        }));
        return true;
      } else {
        setState(s => ({
          ...s,
          purchasing: false,
          error: result.reason === 'cancelled' ? null : result.reason || null,
        }));
        return false;
      }
    } catch (err: any) {
      setState(s => ({ ...s, purchasing: false, error: err.message }));
      return false;
    }
  }, [isNative]);

  // Restore previous purchases
  const restore = useCallback(async () => {
    if (!isNative) return false;

    setState(s => ({ ...s, loading: true, error: null }));

    try {
      const result = await LOGOSSubscription.restorePurchases();
      setState(s => ({
        ...s,
        isPremium: result.isPremium,
        loading: false,
        productId: result.productId,
        expirationDate: result.expirationDate,
        willAutoRenew: result.willAutoRenew,
      }));
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        isPremium: result.isPremium,
        productId: result.productId,
        expirationDate: result.expirationDate,
        willAutoRenew: result.willAutoRenew,
      }));
      return result.isPremium;
    } catch (err: any) {
      setState(s => ({ ...s, loading: false, error: err.message }));
      return false;
    }
  }, [isNative]);

  // Open subscription management in App Store
  const manage = useCallback(async () => {
    if (!isNative) return;
    try {
      await LOGOSSubscription.manageSubscription();
    } catch (err: any) {
      console.warn('[Subscription] manage error:', err.message);
    }
  }, [isNative]);

  return {
    ...state,
    isNative,
    purchase,
    restore,
    manage,
  };
}
